import { createSlice } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { PaginationType } from '../../types/utils/pagination.type';
import {
  assignBillingCredentials,
  createBillingCredentials,
  deleteBillingCredentials,
  deleteBillProfile,
  fetchBillProfiles,
  getBillingCredentials,
  fetchRevioBillProfiles,
  getBillProfileDetails,
  importBillProfile,
  updateBillProfile,
} from './billProfiles.action';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import SnackbarUtils from '../../utils/SnackbarUtils';

export type BillProfile = {
  id: number;
  name: string;
  revioId: number;
  revioAccount: 'tbcreseller' | 'telcloud';
  payaLocationId?: string | null;
  payaUserHashKey?: string | null;
  payaUserAPIKey?: string | null;
  payaUserId?: string | null;
  payaDeveloperId?: string | null;
  createdAt?: string;
  modifiedAt?: string;
  updatedAt?: string;
};

export type BillingCredentialsTemplate = {
  id: number;
  displayName: string | null;
  payaLocationId?: string | null;
  payaUserHashKey?: string | null;
  payaUserAPIKey?: string | null;
  payaUserId?: string | null;
  payaDeveloperId?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type State = {
  items: BillProfile[];
  fetchStatus: FETCH_STATUS;
  error: string | null;
  revioList: any[];
  billProfileDetails: BillProfile | null;
  billProfileDetailsFetchStatus: FETCH_STATUS;
  billProfileDetailsUpdateStatus: FETCH_STATUS;
  billProfileDetailsDeleteStatus: FETCH_STATUS;
  routeParams: { q?: string };
  billingCredentialsItems: BillingCredentialsTemplate[];
  billingCredentialsMeta: PaginationType<BillingCredentialsTemplate>['meta'] | null;
  billingCredentialsFetchStatus: FETCH_STATUS;
  billingCredentialsCreateStatus: FETCH_STATUS;
  billingCredentialsDeleteStatus: FETCH_STATUS;
  billingCredentialsRouteParams: { page: number; limit: number; q?: string; order?: string };
};

const initialState: State = {
  items: [],
  fetchStatus: FETCH_STATUS.NULL,
  error: null,
  revioList: [],
  billProfileDetails: null,
  billProfileDetailsFetchStatus: FETCH_STATUS.NULL,
  billProfileDetailsUpdateStatus: FETCH_STATUS.NULL,
  billProfileDetailsDeleteStatus: FETCH_STATUS.NULL,
  routeParams: { q: '' },
  billingCredentialsItems: [],
  billingCredentialsMeta: null,
  billingCredentialsFetchStatus: FETCH_STATUS.NULL,
  billingCredentialsCreateStatus: FETCH_STATUS.NULL,
  billingCredentialsDeleteStatus: FETCH_STATUS.NULL,
  billingCredentialsRouteParams: { page: 1, limit: 10, q: '', order: '-createdAt' },
};

const slice = createSlice({
  name: 'billProfiles',
  initialState,
  reducers: {
    resetBillProfileDetailsStatuses: state => {
      state.billProfileDetailsFetchStatus = FETCH_STATUS.NULL;
      state.billProfileDetailsUpdateStatus = FETCH_STATUS.NULL;
      state.billProfileDetailsDeleteStatus = FETCH_STATUS.NULL;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(SET_ROUTE_PARAMS, (state, action) => {
        const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
        if (payload.reducer === 'billProfiles') {
          state.routeParams = { ...state.routeParams, ...payload.data };
        }
        if (payload.reducer === 'billingCredentials') {
          state.billingCredentialsRouteParams = { ...state.billingCredentialsRouteParams, ...payload.data };
        }
      })
      .addCase(fetchBillProfiles.pending, state => {
        state.fetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(fetchBillProfiles.fulfilled, (state, action) => {
        state.items = action.payload;
        state.fetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(fetchBillProfiles.rejected, (state, action) => {
        state.fetchStatus = FETCH_STATUS.REJECTED;
        state.error = action.error.message || 'Failed to load bill profiles';
      })
      .addCase(fetchRevioBillProfiles.fulfilled, (state, action) => {
        state.revioList = action.payload;
      })
      .addCase(importBillProfile.pending, state => {
        state.fetchStatus = FETCH_STATUS.PENDING;
        state.error = null;
      })
      .addCase(importBillProfile.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.fetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(importBillProfile.rejected, (state, action) => {
        state.fetchStatus = FETCH_STATUS.REJECTED;
        state.error = (action.payload as string) || action.error.message || 'Failed to import bill profile';
      })
      .addCase(getBillProfileDetails.pending, state => {
        state.billProfileDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBillProfileDetails.fulfilled, (state, action) => {
        state.billProfileDetails = action.payload;
        state.billProfileDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getBillProfileDetails.rejected, (state, action) => {
        state.billProfileDetailsFetchStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(updateBillProfile.pending, state => {
        state.billProfileDetailsUpdateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateBillProfile.fulfilled, (state, action) => {
        const idx = state.items.findIndex(i => i.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
        state.billProfileDetails = action.payload;
        state.billProfileDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(updateBillProfile.rejected, state => {
        state.billProfileDetailsUpdateStatus = FETCH_STATUS.REJECTED;
      })
      .addCase(deleteBillProfile.pending, state => {
        state.billProfileDetailsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteBillProfile.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i.id !== action.payload);
        state.billProfileDetails = null;
        state.billProfileDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(deleteBillProfile.rejected, state => {
        state.billProfileDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      })
      .addCase(getBillingCredentials.pending, state => {
        state.billingCredentialsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getBillingCredentials.fulfilled, (state, action) => {
        state.billingCredentialsItems = action.payload.items;
        state.billingCredentialsMeta = action.payload.meta;
        state.billingCredentialsFetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getBillingCredentials.rejected, (state, action) => {
        state.billingCredentialsFetchStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(createBillingCredentials.pending, state => {
        state.billingCredentialsCreateStatus = FETCH_STATUS.PENDING;
      })
      .addCase(createBillingCredentials.fulfilled, (state, action) => {
        state.billingCredentialsItems.push(action.payload);
        state.billingCredentialsCreateStatus = FETCH_STATUS.FULFILLED;
        SnackbarUtils.success('Bill credentials created successfully');
      })
      .addCase(createBillingCredentials.rejected, (state, action) => {
        state.billingCredentialsCreateStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(deleteBillingCredentials.pending, state => {
        state.billingCredentialsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteBillingCredentials.fulfilled, (state, action) => {
        state.billingCredentialsItems = state.billingCredentialsItems.filter(c => c.id !== action.payload);
        state.billingCredentialsDeleteStatus = FETCH_STATUS.FULFILLED;
        SnackbarUtils.success('Bill credentials deleted successfully');
      })
      .addCase(deleteBillingCredentials.rejected, (state, action) => {
        state.billingCredentialsDeleteStatus = FETCH_STATUS.REJECTED;
        SnackbarUtils.error(action.payload);
      })
      .addCase(assignBillingCredentials.fulfilled, (state, action) => {
        const idx = state.items.findIndex(item => item.id === action.payload.id);
        if (idx >= 0) state.items[idx] = { ...state.items[idx], ...action.payload };
        state.billProfileDetails = action.payload;
        SnackbarUtils.success('Billing credentials assigned successfully');
      })
      .addCase(assignBillingCredentials.rejected, (_state, action) => {
        SnackbarUtils.error(action.payload);
      });
  },
});

export const { resetBillProfileDetailsStatuses } = slice.actions;
export default slice.reducer;
